import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { type ReferralCard, type WebSection } from '@wbk/contentful/api';
import { Button, Checkbox, DayPicker, Input, TextArea, Typography } from '@wbk/ui';
import { Link, useRouter } from '@wbk/config';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/Form';
import useLocalization from '@/context/localization/useLocalization';
import { useOnClickOutside } from '@/hooks/use-on-click-outside';

import Radio from '@/components/ui/Radio';
import { sanitizeInput } from '@/utils';
import Select from '@/components/ui/Select/Select';
import { useLaylatAlomrFormHandler } from '@/hooks/use-laylat-alomr';
import {
  COMPANY_LOGO_FILE_TYPES,
  type LaylatAlomrFormSchemaProps,
  MAX_COMPANY_LOGO_SIZE_MB,
  MAX_REG_DOC_SIZE_MB,
  REG_DOC_FILE_TYPES,
  laylatAlomrFormSchema,
  supportClassifications,
} from '@/utils/schema/laylat-alomr';
import WithGrecaptcha from '@/features/WithGrecaptcha';
import TermsModal from './TermsModal';
import { FileInput } from './components/FileInput';
import OtherSupportModal from './components/OtherSupportModal';
import SubmissionDialog from './components/SubmissionDialog';

type Props = {
  section?: WebSection;
  terms?: WebSection | ({ __typename: 'AppReferralCard' } & ReferralCard) | null;
  benefits?: WebSection | ({ __typename: 'AppReferralCard' } & ReferralCard) | null;
};

const RECAPTCHA_V3_KEY = import.meta.env.VITE_PUBLIC_CAPTCHA_V3_KEY as string;
const KEYNOTE_URL_EN = import.meta.env.VITE_PUBLIC_LAYLAT_ALOMR_KEYNOTE_URL_EN as string;
const KEYNOTE_URL_AR = import.meta.env.VITE_PUBLIC_LAYLAT_ALOMR_KEYNOTE_URL_AR as string;

const FormSection = ({ section, terms, benefits }: Props) => {
  const { t } = useTranslation('common');
  const { locale } = useLocalization();
  const router = useRouter();
  const [customOptions, setCustomOptions] = React.useState<{ value: string; label: string }[]>([]);
  const [isOtherModalOpen, setIsOtherModalOpen] = React.useState(false);
  const [isSupportClassificationOpen, setIsSupportClassificationOpen] = React.useState(false);

  const [openCalendar, setOpenCalendar] = React.useState<'start' | 'end' | null>(null);
  const calendarRef = React.useRef<HTMLDivElement>(null);
  const [isTermsOpen, setIsTermsOpen] = React.useState(false);

  useOnClickOutside(calendarRef as React.RefObject<HTMLElement>, () => {
    setOpenCalendar(null);
  });

  const formatDate = (date: Date, locale: string, options: Intl.DateTimeFormatOptions) => {
    return new Intl.DateTimeFormat(locale, options).format(date);
  };

  const form = useForm<LaylatAlomrFormSchemaProps>({
    resolver: zodResolver(laylatAlomrFormSchema(t)),
    defaultValues: {
      supportClassification: [],
      supportCategory: [],
      expectedBeneficiaries: undefined,
    },
  });

  const { mutateAsync, isPending, isSuccess, isError, reset } =
    useLaylatAlomrFormHandler(RECAPTCHA_V3_KEY);

  const supportType = form.watch('supportType');
  const isCompany = supportType === 'Company';
  const isIndividual = supportType === 'Individual';

  const isDeadlinePassed = React.useMemo(() => {
    const deadline = new Date('2026-01-03T23:59:00');
    const now = new Date();
    return now >= deadline;
  }, []);

  async function onSubmit(data: LaylatAlomrFormSchemaProps) {
    // Company logo is not required for individuals
    if (!data?.companyLogo && !isIndividual) {
      form.setError(
        'companyLogo',
        { message: 'Company logo is required' },
        {
          shouldFocus: true,
        }
      );
      return;
    }

    // Registration document is required only for companies
    if (isCompany && !data?.registrationDocument) {
      form.setError(
        'registrationDocument',
        { message: 'Registration document is required' },
        {
          shouldFocus: true,
        }
      );

      return;
    }

    if (!data?.letterOfAuthorization && !isIndividual) {
      form.setError(
        'letterOfAuthorization',
        { message: 'Letter of authorization is required' },
        {
          shouldFocus: true,
        }
      );
      return;
    }

    try {
      await mutateAsync(data);
    } catch (error) {
      console.error('Error submitting form', error);
      form.setError('companyLogo', {
        message: t('sponsor_form.submission.captcha_failed') || 'Submission failed',
      });
    }
  }

  return (
    <WithGrecaptcha recaptchaKey={RECAPTCHA_V3_KEY}>
      <div className='container-padding container'>
        <div className='container rounded-2xl bg-white/[0.03] px-3 py-8'>
          <div className='mx-auto flex max-w-3xl flex-col gap-8'>
            <div>
              <Button
                as={Link}
                href={locale === 'ar' ? KEYNOTE_URL_AR : KEYNOTE_URL_EN}
                target='_blank'
                rel='noopener noreferrer'
                className='rounded-full px-4 py-1'
              >
                {t('sponsor_form.buttons.sponsorship_advantages')}
              </Button>
            </div>
            <div className='rounded-2xl border border-[#FF9B27] bg-white/10'>
              <Typography as='p' variant='body-XS' className='p-2 px-4 md:text-base'>
                <span className='font-semibold text-[#FF9B27]'>{section?.title}</span>{' '}
                {section?.subtitle}
              </Typography>
            </div>

            <div className='flex flex-col gap-4'>
              <Typography as='h5' variant='body-M' className='lg:text-body-L font-semibold'>
                {t('sponsor_form.entity_data')}
              </Typography>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <fieldset disabled={isPending} className='space-y-8'>
                    <div className='flex flex-col gap-4'>
                      <FormField
                        control={form.control}
                        name='supportType'
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.supporter_type')}{' '}
                              <span className='text-red-500'>*</span>
                            </FormLabel>
                            <div className='space-y-2'>
                              <FormControl>
                                <div className='mt-1 grid w-full gap-2 md:grid-cols-3 md:gap-4'>
                                  {[
                                    {
                                      value: 'Individual',
                                      label: t('sponsor_form.supporter_types.individual'),
                                    },
                                    {
                                      value: 'Company',
                                      label: t('sponsor_form.supporter_types.company'),
                                    },
                                    {
                                      value: 'Non Profit Organization',
                                      label: t('sponsor_form.supporter_types.non_profit'),
                                    },
                                  ].map((type) => (
                                    <Radio
                                      key={type.value}
                                      label={type.label}
                                      containerClassName='bg-white/5 rounded-md p-2'
                                      {...field}
                                      id={type.value}
                                      name={field.name}
                                      value={type.value}
                                      checked={field.value === type.value}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        field.onChange(v);
                                        if (v.toLowerCase() !== 'company') {
                                          form.clearErrors('registrationNumber');
                                          form.clearErrors('registrationDocument');
                                        }
                                        if (v.toLowerCase() === 'individual') {
                                          form.clearErrors('companyLogo');
                                          form.clearErrors('registrationDocument');
                                          form.clearErrors('letterOfAuthorization');
                                        }
                                      }}
                                    />
                                  ))}
                                </div>
                              </FormControl>

                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className='grid gap-4 md:grid-cols-2 md:gap-6'>
                      {[
                        {
                          key: 'companyName',
                          label: t('sponsor_form.company_name'),
                          placeholder: isIndividual
                            ? t('sponsor_form.placeholders.your_full_name')
                            : isCompany
                              ? t('sponsor_form.placeholders.company')
                              : t('sponsor_form.placeholders.organization_name'),
                        },
                        ...(!isIndividual
                          ? [
                              {
                                key: 'contactPersonName',
                                label: t('sponsor_form.contact_person_name'),
                                placeholder: t('sponsor_form.placeholders.contact_name'),
                                conditionallyRequired: true,
                              },
                            ]
                          : []),
                        {
                          key: 'registrationNumber',
                          label: t('sponsor_form.registration_number'),
                          placeholder: t('sponsor_form.placeholders.registration_number'),
                          conditionallyRequired: true,
                        },
                        {
                          key: 'phoneNumber',
                          label: t('sponsor_form.phone_number'),
                          placeholder: '+9665xxxxxxxx',
                          type: 'tel',
                        },
                        {
                          key: 'email',
                          label: t('sponsor_form.email'),
                          placeholder: t('sponsor_form.placeholders.email'),
                        },
                      ].map((item) => (
                        <FormField
                          key={item.key}
                          control={form.control}
                          name={item.key as keyof LaylatAlomrFormSchemaProps}
                          render={({ field }) => {
                            const isRegistrationNumberRequired =
                              item.conditionallyRequired && form.watch('supportType') === 'Company';

                            return (
                              <FormItem className='flex flex-col gap-3 md:gap-4'>
                                <FormLabel>
                                  {item.label}{' '}
                                  {isRegistrationNumberRequired || !item.conditionallyRequired ? (
                                    <span className='text-red-500'>*</span>
                                  ) : (
                                    `(${t('sponsor_form.optional')})`
                                  )}
                                </FormLabel>
                                <div className='space-y-2'>
                                  <FormControl>
                                    <Input
                                      className='rounded-sm border border-white/20 bg-white/20 hover:bg-white/20 focus-visible:bg-white/20'
                                      placeholder={item.placeholder}
                                      type={item.type}
                                      {...field}
                                      value={field.value ?? ''}
                                      onChange={(value) => {
                                        if (item.key === 'phoneNumber') {
                                          const cleaned = value.replace(/[^0-9+]/g, '');
                                          field.onChange(cleaned);
                                        } else if (item.key === 'registrationNumber') {
                                          const cleaned = value.replace(/\D/g, '');
                                          if (cleaned.length <= 10) {
                                            field.onChange(cleaned);
                                          }
                                        } else {
                                          field.onChange(value);
                                        }
                                      }}
                                    />
                                  </FormControl>

                                  <FormMessage />
                                </div>
                              </FormItem>
                            );
                          }}
                        />
                      ))}

                      <FormField
                        control={form.control}
                        name='city'
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.city')} <span className='text-red-500'>*</span>
                            </FormLabel>
                            <div className='space-y-2'>
                              <FormControl>
                                <Select
                                  options={(
                                    t('sponsor_form.placeholders.cities', {
                                      returnObjects: true,
                                    }) as string[]
                                  )
                                    .slice()
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((city) => ({
                                      value: city,
                                      text: city,
                                      id: city,
                                    }))}
                                  value={field.value}
                                  onChange={(option) => field.onChange(option.value)}
                                  placeholder={t('sponsor_form.city')}
                                  className='rounded-sm border border-white/20 bg-white/20 hover:bg-white/20 focus-visible:bg-white/20'
                                />
                              </FormControl>

                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name='companyLogo'
                      render={({ field }) => (
                        <FormItem className='flex flex-col gap-3 md:gap-4'>
                          <FormLabel>
                            {t('sponsor_form.company_logo')}{' '}
                            {!isIndividual ? (
                              <span className='text-red-500'>*</span>
                            ) : (
                              `(${t('sponsor_form.optional')})`
                            )}
                          </FormLabel>

                          <div className='space-y-2'>
                            <FormControl>
                              <FileInput
                                fileName={field.value?.name}
                                accept='image/jpeg, image/png'
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  const maxFileSize =
                                    MAX_COMPANY_LOGO_SIZE_MB * 1024 * 1024 + 5 * 1024;
                                  console.log('Selected file:', file);

                                  if (file) {
                                    if (file.size > maxFileSize) {
                                      form.setError(
                                        'companyLogo',
                                        {
                                          message: t('sponsor_form.file_upload.file_size_error', {
                                            maxSize: MAX_COMPANY_LOGO_SIZE_MB,
                                          }),
                                        },
                                        {
                                          shouldFocus: true,
                                        }
                                      );
                                      return;
                                    }

                                    const fileExtension =
                                      '.' + file.name.split('.').pop()?.toLowerCase();
                                    const allowedExtensions = COMPANY_LOGO_FILE_TYPES.split(
                                      ','
                                    ).map((type) => type.trim().toLowerCase());

                                    if (!allowedExtensions.includes(fileExtension)) {
                                      form.setError(
                                        'companyLogo',
                                        {
                                          message: t('sponsor_form.file_upload.file_type_error', {
                                            fileTypes: COMPANY_LOGO_FILE_TYPES,
                                          }),
                                        },
                                        {
                                          shouldFocus: true,
                                        }
                                      );
                                      return;
                                    }

                                    form.clearErrors('companyLogo');
                                    field.onChange(file);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormDescription>
                              {t('sponsor_form.file_upload.file_size_info', {
                                maxSize: MAX_COMPANY_LOGO_SIZE_MB,
                                fileTypes: COMPANY_LOGO_FILE_TYPES,
                              })}
                            </FormDescription>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name='registrationDocument'
                      render={({ field }) => {
                        return (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.registration_document')}{' '}
                              {isCompany ? (
                                <span className='text-red-500'>*</span>
                              ) : (
                                `(${t('sponsor_form.optional')})`
                              )}
                            </FormLabel>

                            <div className='space-y-2'>
                              <FormControl>
                                <FileInput
                                  fileName={field?.value?.name}
                                  accept='application/pdf, image/jpeg, image/png, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .doc, .docx'
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    const maxFileSize =
                                      MAX_REG_DOC_SIZE_MB * 1024 * 1024 + 5 * 1024;
                                    if (file) {
                                      if (file.size > maxFileSize) {
                                        form.setError(
                                          'registrationDocument',
                                          {
                                            message: t('sponsor_form.file_upload.file_size_error', {
                                              maxSize: MAX_REG_DOC_SIZE_MB,
                                            }),
                                          },
                                          {
                                            shouldFocus: true,
                                          }
                                        );

                                        return;
                                      }

                                      const fileExtension =
                                        '.' + file.name.split('.').pop()?.toLowerCase();
                                      const allowedExtensions = REG_DOC_FILE_TYPES.split(',').map(
                                        (type) => type.trim().toLowerCase()
                                      );

                                      if (!allowedExtensions.includes(fileExtension)) {
                                        form.setError(
                                          'registrationDocument',
                                          {
                                            message: t('sponsor_form.file_upload.file_type_error', {
                                              fileTypes: REG_DOC_FILE_TYPES,
                                            }),
                                          },
                                          {
                                            shouldFocus: true,
                                          }
                                        );
                                        return;
                                      }

                                      form.clearErrors('registrationDocument');
                                      field.onChange(file);
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormDescription>
                                {t('sponsor_form.file_upload.file_size_info', {
                                  maxSize: MAX_REG_DOC_SIZE_MB,
                                  fileTypes: REG_DOC_FILE_TYPES,
                                })}
                              </FormDescription>
                              <FormMessage />
                            </div>
                          </FormItem>
                        );
                      }}
                    />

                    {!isIndividual && (
                      <FormField
                        control={form.control}
                        name='letterOfAuthorization'
                        render={({ field }) => {
                          return (
                            <FormItem className='flex flex-col gap-3 md:gap-4'>
                              <FormLabel>
                                {t('sponsor_form.letter_of_authorization')}{' '}
                                <span className='text-red-500'>*</span>
                              </FormLabel>

                              <div className='space-y-2'>
                                <FormControl>
                                  <FileInput
                                    fileName={field?.value?.name}
                                    accept='application/pdf, image/jpeg, image/png, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .doc, .docx'
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      const maxFileSize =
                                        MAX_REG_DOC_SIZE_MB * 1024 * 1024 + 5 * 1024;
                                      if (file) {
                                        if (file.size > maxFileSize) {
                                          form.setError(
                                            'letterOfAuthorization',
                                            {
                                              message: t(
                                                'sponsor_form.file_upload.file_size_error',
                                                {
                                                  maxSize: MAX_REG_DOC_SIZE_MB,
                                                }
                                              ),
                                            },
                                            {
                                              shouldFocus: true,
                                            }
                                          );

                                          return;
                                        }

                                        const fileExtension =
                                          '.' + file.name.split('.').pop()?.toLowerCase();
                                        const allowedExtensions = REG_DOC_FILE_TYPES.split(',').map(
                                          (type) => type.trim().toLowerCase()
                                        );

                                        if (!allowedExtensions.includes(fileExtension)) {
                                          form.setError(
                                            'letterOfAuthorization',
                                            {
                                              message: t(
                                                'sponsor_form.file_upload.file_type_error',
                                                {
                                                  fileTypes: REG_DOC_FILE_TYPES,
                                                }
                                              ),
                                            },
                                            {
                                              shouldFocus: true,
                                            }
                                          );
                                          return;
                                        }

                                        form.clearErrors('letterOfAuthorization');
                                        field.onChange(file);
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormDescription>
                                  {t('sponsor_form.file_upload.file_size_info', {
                                    maxSize: MAX_REG_DOC_SIZE_MB,
                                    fileTypes: REG_DOC_FILE_TYPES,
                                  })}
                                </FormDescription>
                                <FormMessage />
                              </div>
                            </FormItem>
                          );
                        }}
                      />
                    )}

                    <div>
                      <Typography as='h5' variant='body-M' className='lg:text-body-L font-semibold'>
                        {t('sponsor_form.support')}
                      </Typography>
                    </div>

                    <FormField
                      control={form.control}
                      name='supportClassification'
                      render={({ field }) => (
                        <FormItem className='flex flex-col gap-3 md:gap-4'>
                          <FormLabel>
                            {t('sponsor_form.support_classification')}{' '}
                            <span className='text-red-500'>*</span>
                          </FormLabel>

                          <div className='space-y-2'>
                            <FormControl>
                              <Select
                                options={[
                                  ...supportClassifications.map((item) => ({
                                    value: item.value,
                                    text: t(`sponsor_form.support_classifications.${item.label}`),
                                    id: item.value,
                                  })),
                                  ...customOptions.map((item) => ({
                                    value: item.value,
                                    text: item.label,
                                    id: item.value,
                                  })),
                                ]}
                                maxDisplayTags={3}
                                value={field.value}
                                placeholder={t('sponsor_form.placeholders.multi_select')}
                                multiple
                                open={isSupportClassificationOpen}
                                onOpenChange={setIsSupportClassificationOpen}
                                onChange={(v) => {
                                  const lastSelected = v[v.length - 1];
                                  if (lastSelected?.value === 'Other support (please specify)') {
                                    setIsOtherModalOpen(true);
                                    setIsSupportClassificationOpen(false);
                                  } else {
                                    field.onChange(v.map((i) => i.value));
                                  }
                                }}
                                className='rounded-sm border border-white/20 bg-white/20 hover:bg-white/20 focus-visible:bg-white/20'
                              />
                            </FormControl>

                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className='flex flex-col gap-4'>
                      <FormField
                        control={form.control}
                        name='supportCategory'
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.support_category')}{' '}
                              <span className='text-red-500'>*</span>
                            </FormLabel>

                            <div className='space-y-2'>
                              <FormControl>
                                <div className='mt-1 grid w-full gap-3'>
                                  {[
                                    {
                                      value: 'Groom',
                                      label: t('sponsor_form.support_categories.groom'),
                                    },
                                    {
                                      value: 'Bride',
                                      label: t('sponsor_form.support_categories.bride'),
                                    },
                                    {
                                      value: 'Both',
                                      label: t('sponsor_form.support_categories.both'),
                                    },
                                    {
                                      value: 'Event',
                                      label: t('sponsor_form.support_categories.event'),
                                    },
                                  ].map((type) => {
                                    const isBothSelected = field.value?.includes('Both');
                                    const hasBothBrideAndGroom =
                                      field.value?.includes('Bride') &&
                                      field.value?.includes('Groom');
                                    const isDisabled =
                                      isBothSelected &&
                                      (type.value === 'Groom' || type.value === 'Bride');
                                    return (
                                      <div key={type.value} className='rounded-md bg-white/5 p-2'>
                                        <Checkbox
                                          label={
                                            <Typography
                                              as='span'
                                              variant='body-S'
                                              // className='text-[#71717A]'
                                            >
                                              {type.label}
                                            </Typography>
                                          }
                                          className='rounded-md bg-white/5 p-2'
                                          {...field}
                                          id={type.value}
                                          // name={field.name}
                                          value={type.value}
                                          checked={
                                            hasBothBrideAndGroom && type.value === 'Both'
                                              ? true
                                              : field.value?.includes(type.value)
                                          }
                                          disabled={isDisabled}
                                          onChange={(v) => {
                                            if (v) {
                                              const currentValues = field.value || [];
                                              let newValues = [...currentValues, type.value];

                                              // If selecting Both, also select Bride and Groom
                                              if (type.value === 'Both') {
                                                if (!newValues.includes('Bride')) {
                                                  newValues = [...newValues, 'Bride'];
                                                }
                                                if (!newValues.includes('Groom')) {
                                                  newValues = [...newValues, 'Groom'];
                                                }
                                              }

                                              // If both Bride and Groom are now selected, auto-select Both
                                              if (
                                                (type.value === 'Bride' ||
                                                  type.value === 'Groom') &&
                                                newValues.includes('Bride') &&
                                                newValues.includes('Groom')
                                              ) {
                                                if (!newValues.includes('Both')) {
                                                  newValues = [...newValues, 'Both'];
                                                }
                                              }

                                              field.onChange(newValues);
                                            } else {
                                              const currentValues = field.value || [];
                                              let newValues = currentValues.filter(
                                                (val) => val !== type.value
                                              );

                                              // If deselecting Both, remove Bride and Groom
                                              if (type.value === 'Both') {
                                                newValues = newValues.filter(
                                                  (val) => val !== 'Bride' && val !== 'Groom'
                                                );
                                              }

                                              field.onChange(newValues);
                                            }
                                          }}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </FormControl>
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name='expectedBeneficiaries'
                      render={({ field }) => (
                        <FormItem className='flex flex-col gap-3 md:gap-4'>
                          <FormLabel>
                            {t('sponsor_form.expected_beneficiaries')}{' '}
                            <span className='text-red-500'>*</span>
                          </FormLabel>
                          <div className='space-y-2'>
                            <FormControl>
                              <Input
                                type='text'
                                inputMode='numeric'
                                className='rounded-sm border border-white/20 bg-white/20 [appearance:textfield] hover:bg-white/20 focus-visible:bg-white/20 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                                placeholder={t('sponsor_form.placeholders.beneficiary')}
                                value={field.value}
                                onChange={(value) => {
                                  const cleaned = value.replace(/[^0-9]/g, '');
                                  if (
                                    cleaned === '' ||
                                    (parseInt(cleaned) >= 1 && parseInt(cleaned) <= 1000)
                                  ) {
                                    field.onChange(cleaned === '' ? '' : parseInt(cleaned));
                                  }
                                }}
                                min='1'
                                max='1000'
                                onWheel={(e) => e.currentTarget.blur()}
                              />
                            </FormControl>

                            <FormDescription>
                              {t('sponsor_form.placeholders.beneficiary')}
                            </FormDescription>
                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className='flex flex-col gap-4'>
                      <FormField
                        control={form.control}
                        name='supportDeliveryMechanism'
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.support_delivery_mechanism')}{' '}
                              <span className='text-red-500'>*</span>
                            </FormLabel>
                            <div className='space-y-2'>
                              <FormControl>
                                <div className='mt-1 grid w-full gap-2 md:grid-cols-2 md:gap-4'>
                                  {[
                                    {
                                      value: 'Delivery',
                                      label: t('sponsor_form.delivery_mechanisms.delivery'),
                                    },
                                    {
                                      value: 'Pickup',
                                      label: t('sponsor_form.delivery_mechanisms.pickup'),
                                    },
                                  ].map((type) => (
                                    <Radio
                                      key={type.value}
                                      label={type.label}
                                      containerClassName='bg-white/5 rounded-md p-2'
                                      {...field}
                                      id={type.value}
                                      name={field.name}
                                      value={type.value}
                                      checked={field.value === type.value}
                                      onChange={(e) => field.onChange(e.target.value)}
                                    />
                                  ))}
                                </div>
                              </FormControl>

                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className='grid gap-4 md:grid-cols-3'>
                      {[
                        {
                          key: 'estimatedSupportValue',
                          label: t('sponsor_form.estimated_support_value'),
                          placeholder: t('sponsor_form.placeholders.min_100_sar'),
                          type: 'number',
                        },
                      ].map((item) => (
                        <FormField
                          key={item.key}
                          control={form.control}
                          name={item.key as keyof LaylatAlomrFormSchemaProps}
                          render={({ field }) => (
                            <FormItem className='flex flex-col justify-between gap-3 md:gap-4'>
                              <FormLabel>
                                {item.label} <span className='text-red-500'>*</span>
                              </FormLabel>
                              <div className='space-y-2'>
                                <FormControl>
                                  <Input
                                    type='text'
                                    inputMode='numeric'
                                    lang='fr-CA'
                                    className='rounded-sm border border-white/20 bg-white/20 uppercase [appearance:textfield] hover:bg-white/20 focus-visible:bg-white/20 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
                                    placeholder={item.placeholder}
                                    {...field}
                                    min={item.type === 'number' ? '100' : undefined}
                                    onWheel={(e) => e.currentTarget.blur()}
                                    onChange={(value) => {
                                      if (item.type === 'number') {
                                        const cleaned = value.replace(/[^0-9]/g, '');
                                        if (cleaned === '' || parseFloat(cleaned) >= 0) {
                                          field.onChange(cleaned);
                                        }
                                      } else {
                                        field.onChange(value);
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormMessage />
                              </div>
                            </FormItem>
                          )}
                        />
                      ))}

                      <FormField
                        control={form.control}
                        name='supportStartDate'
                        render={({ field }) => (
                          <FormItem className='flex flex-col justify-between gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.support_start_date')}{' '}
                              <span className='text-red-500'>*</span>
                            </FormLabel>
                            <div className='relative space-y-2'>
                              <FormControl>
                                <button
                                  type='button'
                                  className='flex h-10 w-full cursor-pointer items-center rounded-sm border border-white/20 bg-white/20 px-3 text-sm hover:bg-white/20'
                                  onClick={() => setOpenCalendar('start')}
                                >
                                  {field.value ? (
                                    formatDate(new Date(field.value), locale, {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })
                                  ) : (
                                    <span className='text-gray-400'>
                                      {t('sponsor_form.placeholders.date')}
                                    </span>
                                  )}
                                </button>
                              </FormControl>
                              {openCalendar === 'start' && (
                                <div
                                  ref={calendarRef}
                                  className='absolute left-0 top-full z-50 mt-2 rounded-lg border border-white/10 bg-[#1E1E1E] p-2 shadow-xl'
                                >
                                  <DayPicker
                                    className='bg-body-level-1 w-full rounded-lg py-2'
                                    lang={locale}
                                    mode='single'
                                    selected={field.value ? new Date(field.value) : undefined}
                                    onSelect={(date) => {
                                      field.onChange(date ? date.toISOString() : '');
                                      setOpenCalendar(null);
                                    }}
                                    weekStartsOn={0}
                                    startMonth={new Date(new Date().getFullYear(), 0, 1)}
                                    endMonth={new Date(new Date().getFullYear(), 0, 31)}
                                  />
                                </div>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name='supportEndDate'
                        render={({ field }) => (
                          <FormItem className='flex flex-col justify-between gap-3 md:gap-4'>
                            <FormLabel>
                              {t('sponsor_form.support_end_date')}{' '}
                              <span className='text-red-500'>*</span>
                            </FormLabel>
                            <div className='relative space-y-2'>
                              <FormControl>
                                <button
                                  type='button'
                                  className='flex h-10 w-full cursor-pointer items-center rounded-sm border border-white/20 bg-white/20 px-3 text-sm hover:bg-white/20'
                                  onClick={() => setOpenCalendar('end')}
                                >
                                  {field.value ? (
                                    formatDate(new Date(field.value), locale, {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })
                                  ) : (
                                    <span className='text-gray-400'>
                                      {t('sponsor_form.placeholders.date')}
                                    </span>
                                  )}
                                </button>
                              </FormControl>
                              {openCalendar === 'end' && (
                                <div
                                  ref={calendarRef}
                                  className='absolute left-0 top-full z-50 mt-2 rounded-lg border border-white/10 bg-[#1E1E1E] p-2 shadow-xl'
                                >
                                  <DayPicker
                                    className='bg-body-level-1 w-full rounded-lg py-2'
                                    lang={locale}
                                    mode='single'
                                    selected={field.value ? new Date(field.value) : undefined}
                                    onSelect={(date) => {
                                      field.onChange(date ? date.toISOString() : '');
                                      setOpenCalendar(null);
                                    }}
                                    weekStartsOn={0}
                                    startMonth={
                                      form.getValues('supportStartDate')
                                        ? new Date(form.getValues('supportStartDate')!)
                                        : new Date(new Date().getFullYear(), 0, 1)
                                    }
                                    endMonth={new Date(new Date().getFullYear(), 11, 31)}
                                  />
                                </div>
                              )}
                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name='detailedSupportDescription'
                      render={({ field }) => (
                        <FormItem className='flex flex-col justify-between gap-3 md:gap-4'>
                          <FormLabel>{t('sponsor_form.detailed_support_description')}</FormLabel>
                          <div className='space-y-2'>
                            <FormControl>
                              <TextArea
                                className='min-h-40 rounded-sm border border-white/5 bg-white/5 hover:bg-white/5 focus-visible:bg-white/5'
                                placeholder={t('sponsor_form.placeholders.max_500_chars')}
                                {...field}
                                onChange={(v) => {
                                  const text = sanitizeInput(v);
                                  field.onChange(text);
                                }}
                              />
                            </FormControl>

                            <FormMessage />
                          </div>
                        </FormItem>
                      )}
                    />

                    <div className='flex flex-col gap-4'>
                      <FormField
                        control={form.control}
                        name='termsAccepted'
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-3 md:gap-4'>
                            <div className='space-y-2'>
                              <FormControl>
                                <div className='rounded-md bg-white/5 p-2'>
                                  <Checkbox
                                    className='rounded-md bg-white/5'
                                    label={
                                      <Typography
                                        as='span'
                                        variant='body-XS'
                                        className='lg:text-body-S text-[#71717A]'
                                      >
                                        {t('sponsor_form.terms.agree')}{' '}
                                        <button
                                          type='button'
                                          onClick={() => setIsTermsOpen(true)}
                                          className='text-primary text-body-XS lg:text-body-S underline transition-colors hover:text-white'
                                        >
                                          {t('sponsor_form.terms.terms_conditions')}
                                        </button>
                                        .
                                      </Typography>
                                    }
                                    {...field}
                                    value=''
                                    checked={field.value}
                                    onChange={(v) => field.onChange(v)}
                                    id='terms'
                                    name={field.name}
                                  />
                                </div>
                              </FormControl>

                              <FormMessage />
                            </div>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className='flex items-center gap-2'>
                      {!isDeadlinePassed && (
                        <Button
                          type='submit'
                          className='rounded-full px-4 py-1'
                          disabled={isPending}
                        >
                          {t('sponsor_form.buttons.submit')}
                        </Button>
                      )}
                      <Button
                        as={Link}
                        href={`/${locale}`}
                        type='button'
                        theme='transparent'
                        className='rounded-full bg-white/20 px-4 py-1 hover:bg-white/30'
                      >
                        {t('sponsor_form.buttons.cancel')}
                      </Button>
                    </div>
                  </fieldset>
                </form>
              </Form>
            </div>
          </div>
        </div>

        <SubmissionDialog
          isPending={isPending}
          isSuccess={isSuccess}
          isError={isError}
          onReset={reset}
          onGoHome={() => router.push(`/${locale}`)}
        />

        <OtherSupportModal
          isOpen={isOtherModalOpen}
          onClose={() => setIsOtherModalOpen(false)}
          onAdd={(value) => {
            const newOption = { value, label: value };
            setCustomOptions([...customOptions, newOption]);
            const currentValues = form.getValues('supportClassification') || [];
            form.setValue('supportClassification', [...currentValues, value]);
            form.clearErrors('supportClassification');
          }}
        />
        <TermsModal
          isOpen={isTermsOpen}
          onClose={() => setIsTermsOpen(false)}
          onAgree={() => {
            form.setValue('termsAccepted', true, { shouldValidate: true });
            setIsTermsOpen(false);
          }}
          terms={terms}
          benefits={benefits}
        />
      </div>
    </WithGrecaptcha>
  );
};

export default FormSection;
